// Copy, variant mapping, and suppression logic for the newsletter subscribe
// modal. Decision logic lives here as pure functions so a JS test runner can
// cover it later without refactoring; a Ruby guard test asserts on this file's
// source (copy strings, mapping entries, and that it contains no em dashes).

export type NewsletterVariant = 'events' | 'resources'
export type NewsletterTrigger = 'exit_intent' | 'scroll_depth'

export const NEWSLETTER_MODAL_COPY = {
  events: {
    headline: "Discover the season's charitable events",
    body: 'Galas, run/walks, and benefit nights worth showing up for, sent to your inbox.',
    button: 'Send me events',
  },
  resources: {
    headline: 'Run of Show, in your inbox',
    body: 'Playbooks, benchmarks, and reporting for the people who plan charitable events.',
    button: 'Send me Run of Show',
  },
  footnote: 'Monthly. Unsubscribe anytime.',
  success: {
    title: "You're subscribed!",
    body: "We'll keep you updated on upcoming galas and charitable events.",
  },
} as const

// Inertia page components that get the "resources" variant: the Run of Show
// blog, vendor services marketing pages, the nonprofit marketing page, and
// the planning tools. Everything else gets "events".
export const RESOURCES_COMPONENT_PREFIXES = ['blog/', 'event_services/'] as const
export const RESOURCES_COMPONENTS = [
  'for_nonprofits/show',
  'event_announcement_planner/show',
  'gala_ticket_pricing_planner/show',
  'seating_planner/show',
  'tax_deduction_calculator/show',
] as const

export function variantForComponent(component: string): NewsletterVariant {
  if (RESOURCES_COMPONENT_PREFIXES.some((prefix) => component.startsWith(prefix))) {
    return 'resources'
  }
  if ((RESOURCES_COMPONENTS as readonly string[]).includes(component)) {
    return 'resources'
  }
  return 'events'
}

export const SUPPRESSED_PREFIXES = [
  '/users/sign_in',
  '/users/sign_up',
  '/users/password_resets',
  '/users/complete_profile',
  '/organizations/sign_in',
  '/organizations/sign_up',
  '/organizations/password_resets',
  '/organizations/complete_profile',
  '/organizations/invitations',
  '/checkout',
  '/contact',
  '/services/submit',
  '/subscribed',
]

export function isSuppressedPath(url: string): boolean {
  const path = url.split('?')[0]
  return (
    SUPPRESSED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`)) ||
    path.startsWith('/events/')
  )
}

export const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

export type ModalGateInput = {
  enabled: boolean
  loggedIn: boolean
  url: string
  submitted: boolean
  dismissedAtMs: number | null
  nowMs: number
}

export function shouldArmModal(gate: ModalGateInput): boolean {
  if (!gate.enabled || gate.loggedIn || gate.submitted) return false
  if (isSuppressedPath(gate.url)) return false
  // A future dismissedAtMs (clock skew) gives a negative diff, which stays
  // below the cooldown and keeps the modal suppressed.
  if (gate.dismissedAtMs !== null && gate.nowMs - gate.dismissedAtMs < DISMISS_COOLDOWN_MS) {
    return false
  }
  return true
}

export const STORAGE_KEYS = {
  submitted: 'gala:newsletter-modal:submitted',
  dismissedAt: 'gala:newsletter-modal:dismissed-at',
} as const

export const LEGACY_SESSION_KEY = 'gala:newsletter-modal:shown'

export function readModalStorage(): { submitted: boolean; dismissedAtMs: number | null } | null {
  try {
    const submitted = localStorage.getItem(STORAGE_KEYS.submitted) === '1'
    const rawDismissedAt = localStorage.getItem(STORAGE_KEYS.dismissedAt)
    const parsed = rawDismissedAt === null ? NaN : Number(rawDismissedAt)
    return { submitted, dismissedAtMs: Number.isFinite(parsed) ? parsed : null }
  } catch {
    return null
  }
}

export function recordSubmitted(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.submitted, '1')
  } catch {
    /* storage unavailable */
  }
}

export function recordDismissed(nowMs: number = Date.now()): void {
  try {
    localStorage.setItem(STORAGE_KEYS.dismissedAt, String(nowMs))
  } catch {
    /* storage unavailable */
  }
}

export function cleanupLegacySessionKey(): void {
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY)
  } catch {
    /* storage unavailable */
  }
}

export type NewsletterModalGtagParams = {
  variant: NewsletterVariant
  page_type: string
  trigger: NewsletterTrigger
}

export function fireNewsletterModalGtag(
  name: 'subscribe_modal_shown' | 'subscribe_modal_dismissed' | 'subscribe_modal_submitted',
  params: NewsletterModalGtagParams,
): void {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params)
  }
}
